import React from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';

const defaultTextFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '0.75rem',
  },
};

const formLabelClassName = 'block text-sm font-medium leading-6 text-stone-700 mb-2';
const inlineLabelClassName = 'mb-2 block text-sm font-medium text-stone-800';

/**
 * Dependent Category / Subcategory autocomplete pair.
 * Subcategory is cleared and disabled while options load after a category change.
 */
export default function CategorySubcategoryFields({
  categories = [],
  category = null,
  subCategories = [],
  subCategory = null,
  loadingSubCategories = false,
  subCategoryFieldKey = 0,
  onCategoryChange,
  onSubCategoryChange,
  layout = 'form',
  size = 'small',
  required = false,
  categoryLabel = 'Category',
  subCategoryLabel = 'Sub Category',
  categoryId = 'category-field',
  subCategoryId = 'subcategory-field',
  disabled = false,
  textFieldSx = defaultTextFieldSx,
  className = '',
}) {
  const isInline = layout === 'inline';
  const labelClassName = isInline ? inlineLabelClassName : formLabelClassName;
  const requiredMark = required ? <span className=""> *</span> : null;

  const containerClassName = isInline
    ? className
    : `grid grid-cols-1 md:grid-cols-2 gap-4 ${className}`.trim();

  const renderCategoryField = () => {
    if (isInline) {
      return (
        <Autocomplete
          disablePortal
          id={categoryId}
          options={categories}
          value={category}
          onChange={(_event, newValue) => onCategoryChange(newValue)}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          getOptionLabel={(option) => option?.name || ''}
          size={size}
          disabled={disabled}
          sx={{ minWidth: 180 }}
          renderInput={(params) => <TextField {...params} label={categoryLabel} />}
        />
      );
    }

    return (
      <div>
        <label className={labelClassName} htmlFor={categoryId}>
          {categoryLabel}
          {requiredMark}
        </label>
        <Autocomplete
          disablePortal
          id={categoryId}
          options={categories}
          value={category}
          onChange={(_event, newValue) => onCategoryChange(newValue)}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          getOptionLabel={(option) => option?.name || ''}
          size={size}
          disabled={disabled}
          renderInput={(params) => (
            <TextField {...params} sx={textFieldSx} placeholder={`Select ${categoryLabel.toLowerCase()}`} />
          )}
        />
      </div>
    );
  };

  const renderSubCategoryField = () => {
    const subCategoryDisabled =
      disabled || !category || loadingSubCategories || subCategories.length === 0;

    const subCategoryInput = (
      <Autocomplete
        key={`${subCategoryId}-${subCategoryFieldKey}`}
        disablePortal
        id={subCategoryId}
        options={subCategories}
        value={subCategory}
        onChange={(_event, newValue) => onSubCategoryChange(newValue)}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        getOptionLabel={(option) => option?.name || ''}
        size={size}
        disabled={subCategoryDisabled}
        loading={loadingSubCategories}
        renderInput={(params) => (
          <TextField
            {...params}
            label={isInline ? subCategoryLabel : undefined}
            placeholder={
              loadingSubCategories
                ? 'Loading subcategories...'
                : !category
                  ? 'Select a category first'
                  : `Select ${subCategoryLabel.toLowerCase()}`
            }
            sx={isInline ? undefined : textFieldSx}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingSubCategories ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    );

    if (isInline) {
      return (
        <div style={{ minWidth: 180 }}>
          {subCategoryInput}
        </div>
      );
    }

    return (
      <div>
        <label className={labelClassName} htmlFor={subCategoryId}>
          {subCategoryLabel}
          {requiredMark}
        </label>
        {subCategoryInput}
      </div>
    );
  };

  if (isInline) {
    return (
      <>
        {renderCategoryField()}
        {renderSubCategoryField()}
      </>
    );
  }

  return (
    <>
      {renderCategoryField()}
      {renderSubCategoryField()}
    </>
  );
}
